import AdvancedButtons from './AdvancedButtons';
import DatatypeDisplay from '../core/DatatypeDisplay';
import Notification from '../core/Notification';
import ComplexExpressionButton from '../core/ComplexExpressionButton';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Tooltip from '@material-ui/core/Tooltip';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import Errored from '../core/Errored';
import SecondaryButton from '../core/SecondaryButton';
import { globalStyles as styles } from '../core/style/gstheme';
import TypedLiteralDialog from '../core/TypedLiteralDialog';
import { BN_NAMESPACE, NUMERIC_DATATYPES, DATATYPES } from '../core/utils/constants';
import { makeCancelable, computeDatatype, isSupportedDatatype, deleteBlankNode } from '../core/utils/utils';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import BlankNodeHandler from './BlankNodeHandler';
import DatatypeDialog from './DatatypeDialog';

// See:
// https://www.w3.org/TR/2012/REC-owl2-syntax-20121211/#Datatype_Restrictions
// For withRestriction constraints. We are not supporting langRange for now
// https://www.w3.org/TR/2012/REC-owl2-quick-reference-20121211/#Facets

const NUMBER_DATATYPE = "Number";
const STRING_DATATYPE = "String";

const propsList = ["intersectionOf", "unionOf", "oneOf", "complementOf", "onDatatype"];

class DatatypeHandler extends React.Component {

  constructor(props) {
    super(props);
    let datatypeFormat = STRING_DATATYPE;
    if (NUMERIC_DATATYPES.includes(props.data.onDatatype)) datatypeFormat = NUMBER_DATATYPE;
    this.state = {
      isErrored: undefined,
      isLoading: true,
      valueError: false,
      datatypeFormat,
      operator: undefined,
      constraintType: undefined,
      wrOrder: undefined,
      withRestrictions: props.withRestrictions ? props.withRestrictions.length : 0,
      used: [false, false, false, false]
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.getWithData();
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  loadData(iris) {
    this.cancellable = makeCancelable(GS.loadData(iris, {}, this.props.graph));
    return this.cancellable;
  }

  static getDerivedStateFromProps(props, state) {
    const wrLength = "withRestrictions" in props.data ?
      props.data.withRestrictions === undefined ?
        0
        :
        props.data.withRestrictions.length
      :
      0;
    const dataProps = Object.keys(props.data).filter(k => propsList.includes(k) && props.data[k] !== undefined);
    if (wrLength !== state.withRestrictions) {
      return {
        isLoading: true,
        withRestrictions: wrLength,
        ...(state.constraintType && dataProps.length > 0) && { constraintType: undefined }
      };
    }
    if (state.constraintType && dataProps.length > 0) {
      return {
        constraintType: undefined
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isErrored && this.state.isLoading === true) {
      this.getWithData();
    }
  }

  getWithData = async () => {
    try {
      const graph = this.props.graph;
      const operator = this.state.operator;
      const withRestrictions = this.props.data.withRestrictions;
      // >=, <=, >, <
      let used = [false, false, false, false];
      // More work needed on valueError to compute if values are updated
      let valueError = false;
      let min = undefined;
      let max = undefined;
      let wrOrder = [];
      let cancelOperator = false;
      // wrOrder is used to ensure constraints always appear in the same order
      if (withRestrictions && withRestrictions.length > 0) {
        await this.loadData(withRestrictions, graph).promise;
        if (this.cancellable && this.cancellable.isCancelled()) return;
        GS.handle("get-with-data");
        withRestrictions.forEach(wr => {
          if (GS.isLoaded(wr, graph)) {
            const data = GS.data(wr, graph);
            if ("minInclusive" in data) {
              used[0] = true;
              if (max && data.minInclusive["@value"] > max) valueError = true;
              min = data.minInclusive["@value"];
              wrOrder[0] = wr;
              if (operator === "minInclusive") cancelOperator = true;
            }
            if ("maxInclusive" in data) {
              used[1] = true;
              if (min && data.maxInclusive["@value"] <= min) valueError = true;
              max = data.maxInclusive["@value"];
              wrOrder[2] = wr;
              if (operator === "maxInclusive") cancelOperator = true;
            }
            if ("minExclusive" in data) {
              used[2] = true;
              if (max && data.minExclusive["@value"] >= max) valueError = true;
              min = data.minExclusive["@value"];
              wrOrder[1] = wr;
              if (operator === "minExclusive") cancelOperator = true;
            }
            if ("maxExclusive" in data) {
              used[3] = true;
              if (min && data.maxExclusive["@value"] <= min) valueError = true;
              max = data.maxExclusive["@value"];
              wrOrder[3] = wr;
              if (operator === "maxExclusive") cancelOperator = true;
            }
            if ("minLength" in data) {
              used[0] = true;
              if (max && data.minLength["@value"] > max) valueError = true;
              min = data.minLength["@value"];
              wrOrder[4] = wr;
              if (operator === "minLength") cancelOperator = true;
            }
            if ("maxLength" in data) {
              used[1] = true;
              if (min && data.maxLength["@value"] < min) valueError = true;
              max = data.maxLength["@value"];
              wrOrder[5] = wr;
              if (operator === "maxLength") cancelOperator = true;
            }
            if ("length" in data) {
              used[2] = true;
              wrOrder[6] = wr;
              if (operator === "length") cancelOperator = true;
            }
            if (data.pattern) {
              used[3] = true;
              wrOrder[7] = wr;
              if (operator === "pattern") cancelOperator = true;
            }
            if (data.rdfLangRange) {
              wrOrder[8] = wr;
            }
          }
        });
      }
      else {
        used = [false, false, false, false];
        wrOrder = undefined;
      }
      this.setState({
        isLoading: false,
        valueError,
        used,
        wrOrder,
        ...(cancelOperator) && { operator: undefined }
      });
    }
    catch (e) {
      if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
    }
  };

  addOnDatatype = (datatype) => {
    GS.addProperty(this.props.id, this.props.graph, "onDatatype", datatype);
    let datatypeFormat = STRING_DATATYPE;
    if (NUMERIC_DATATYPES.includes(datatype)) datatypeFormat = NUMBER_DATATYPE;
    this.setState({ datatypeFormat, constraintType: undefined });
  };

  addComplementDatatype = (iri) => {
    GS.addProperty(this.props.id, this.props.graph, "datatypeComplementOf", iri, null, null, this.props.graph);
    this.setConstraintType(undefined);
  };

  selectOperator = (operator) => this.setState({ operator });

  removeOperator = () => this.setState({ operator: undefined });

  addWith = async (value, datatype) => {
    const { graph, id, sourceComponent } = this.props;
    const withIRI = BN_NAMESPACE + uuidv4();
    let newWith = {
      id: withIRI,
      type: "DatatypeConstraint",
      [this.state.operator]: {
        "@value": value,
        "type": datatype
      },
      invisibility: sourceComponent
    };
    await GS.insertResource(newWith, withIRI, this.props.graph, [id]);
    GS.insert(id, graph, "withRestrictions", withIRI);
    this.setState({ operator: undefined });
  };

  removeWith = (position) => GS.remove(this.props.id, this.props.graph, "withRestrictions", position);

  addResourceToList = (resourceIRI) => {
    const { graph, id, data } = this.props;
    const property = this.state.constraintType ?
      this.state.constraintType
      :
      "unionOf" in data ?
        "unionOf"
        :
        "intersectionOf";
    GS.insert(id, graph, property, resourceIRI);
    if (this.state.constraintType) this.setState({ constraintType: undefined });
  };

  addLiteralToList = (text, datatype) => {
    const { graph, id } = this.props;
    GS.insertLiteral(id, graph, "oneOf", text, null, datatype);
    if (this.state.constraintType) this.setState({ constraintType: undefined });
  };

  addDatatype = async (property) => {
    const { graph } = this.props;
    const datatypeIRI = BN_NAMESPACE + uuidv4();
    let newDatatype = {
      id: datatypeIRI,
      invisibility: this.props.sourceComponent,
      type: "RdfsDatatype"
    };
    await GS.insertResource(newDatatype, datatypeIRI, graph, [this.props.id]);
    GS.addProperty(this.props.id, graph, property, datatypeIRI);
  };

  addDatatypeToList = (forceProp) => async () => {
    const { graph, id, data } = this.props;
    const datatypeIRI = BN_NAMESPACE + uuidv4();
    let newDatatype = {
      id: datatypeIRI,
      invisibility: this.props.sourceComponent,
      type: "RdfsDatatype"
    };
    const property = forceProp ?
      forceProp
      :
      "unionOf" in data ?
        "unionOf"
        :
        "intersectionOf" in data ?
          "intersectionOf"
          :
          "oneOf";
    await GS.insertResource(newDatatype, datatypeIRI, graph, [id]);
    GS.insert(id, graph, property, datatypeIRI);
  };

  deleteListItem = (listProp, position, iri) => GS.remove(iri, this.props.graph, listProp, position);

  deleteResource = () => deleteBlankNode(this.props.id, this.props.graph);

  setConstraintType = (constraintType) => this.setState({ constraintType });

  exitConstraint = () => this.setState({ constraintType: undefined });

  render() {

    const { data, id, graph, components, viewLang, currentRange, useWorkflow,
      defaultLang, projectIRI, context, sourceComponent, classes } = this.props;
    const { isErrored, used, operator, datatypeFormat, constraintType, wrOrder } = this.state;

    if (isErrored) return <Errored dataTest="dt-error" data={isErrored} />;
    const isReadOnly = this.props.isReadOnly || data.pendingDeletion;

    const { onDatatype, withRestrictions } = data;

    const conflictingProps = Object.keys(data).filter(k => propsList.includes(k) && data[k] !== undefined);
    if (conflictingProps.length > 1) {
      return (
        <div data-test="restriction-container" className={classes.incompleteData}>
          {!isReadOnly &&
            <Tooltip title='Delete data range'>
              <span style={{ float: 'right', marginBottom: 6 }}>
                <Typography variant="body2">
                  <IconButton data-test="delete-datarange" size="small" onClick={this.deleteResource} className={classes.deleteButton}>
                    <DeleteIcon />
                  </IconButton>
                </Typography>
              </span>
            </Tooltip>
          }
          <div style={{ clear: 'both' }}>
            {data.pendingDeletion ?
              <Notification error={true} text="This datatype is pending deletion." dataTest="pending-deletion" />
              :
              <Notification text={"This datatype contains conflicting properties: " + conflictingProps.join(", ") + ". Please delete."} dataTest="conflicting" />
            }
          </div>
        </div>
      );
    }

    let label = undefined;
    let propName = undefined;

    if ("intersectionOf" in data) {
      label = "Intersection Of";
      propName = "intersectionOf";
    }
    else if ("unionOf" in data) {
      label = "Union Of";
      propName = "unionOf";
    }
    else if ("datatypeComplementOf" in data) {
      label = "Complement Of";
      propName = "datatypeComplementOf";
    }
    else if ("oneOf" in data) {
      label = "One Of";
      propName = "oneOf";
    }

    const items = data.onDatatype || data.intersectionOf || data.unionOf || data.datatypeComplementOf || data.oneOf;
    const itemsList = Array.isArray(items) ? items : [items];

    // This code is also in DatatypeDisplay
    const existingTypedLiteralValues = items ?
      itemsList
        .filter(value => !value.language && !value.id && !(typeof value === "boolean"))
        .map(content => {
          const dt = computeDatatype(content);
          const tc = typeof content === "object";
          const text = tc ?
            content["@value"]
            :
            content;
          return {
            text,
            datatype: tc ?
              content["type"]
              :
              dt || DATATYPES.STRING
          };
        })
      :
      [];

    const isComplete = withRestrictions && withRestrictions.length > 0;

    const operatorTitle = operator === "minInclusive" ?
      "Add Minimum Inclusive Value"
      :
      operator === "maxInclusive" ?
        "Add Maximum Inclusive Value"
        :
        operator === "minExclusive" ?
          "Add Minimum Exclusive Value"
          :
          operator === "maxExclusive" ?
            "Add Maximum Exclusive Value"
            :
            operator === "minLength" ?
              "Add Minimum Length"
              :
              operator === "maxLength" ?
                "Add Maximum Length"
                :
                operator === "length" ?
                  "Add Length"
                  :
                  "Add Pattern";

    if (constraintType === "datatypeComplementOf") {
      return <div>
        <Typography paragraph variant="caption" className={classes.instruction}>Choose datatype to constrain on</Typography>
        <DatatypeDialog
          sourceComponent={sourceComponent}
          onAddDatatype={this.addComplementDatatype}
          open={true}
          graph={graph}
          viewLang={viewLang}
          useButton={true}
          disabled={false}
          title="Add Datatype"
          name="Datatype" />
      </div>;
    }

    const isDatatypeSupported = isSupportedDatatype(currentRange);
    if (constraintType === "onDatatype") {
      return <div>
        <Typography paragraph variant="caption" className={classes.instruction}>Choose datatype to constrain on</Typography>
        <DatatypeDialog
          sourceComponent={sourceComponent}
          onAddDatatype={this.addOnDatatype}
          onExitDatatype={this.exitConstraint}
          open={true}
          allowCustom={false}
          graph={graph}
          viewLang={viewLang}
          useButton={true}
          disabled={currentRange ? isDatatypeSupported : false}
          datatype={currentRange}
          title="Add Datatype"
          name="Datatype" />
      </div>;
    }

    if (constraintType === "oneOf") {
      return <TypedLiteralDialog
        key={currentRange}
        onAddLiteral={this.addLiteralToList}
        existingValues={existingTypedLiteralValues}
        open={true}
        datatypeFixed={currentRange ? true : false}
        datatype={currentRange}
        title="Add Literal To One Of"
        onExitLiteral={this.exitConstraint}
        placeholder="Enter value"
        name="Text" />;
    }

    if (constraintType) {
      return <DatatypeDialog
        sourceComponent={sourceComponent}
        onAddDatatype={this.addResourceToList}
        onExitDatatype={this.exitConstraint}
        open={true}
        graph={graph}
        viewLang={viewLang}
        useButton={true}
        disabled={currentRange ? true : false}
        datatype={currentRange}
        title="Add Datatype"
        name="Datatype" />;
    }

    if (data.pendingDeletion) return null;
    
    if (!items || items.length === 0) return <div className={classes.incompleteData} style={{ marginBottom: 12, clear: 'both' }}>
      {data.pendingDeletion &&
        <Notification error={true} text="This datatype is pending deletion." dataTest="pending-deletion" />
      }
      {isReadOnly && !data.pendingDeletion &&
        <Notification text="This contains partially completed structures." dataTest="empty-partial" />
      }
      {!isReadOnly &&
        <div>
          <Tooltip title='Delete data range'>
            <span style={{ float: 'right', marginBottom: 6 }}>
              <Typography variant="body2">
                <IconButton data-test="delete-datarange" size="small" onClick={this.deleteResource} className={classes.deleteButton}>
                  <DeleteIcon />
                </IconButton>
              </Typography>
            </span>
          </Tooltip>
          <Typography variant="caption" className={classes.instruction}>Choose from</Typography>
          <span className={classes.optionsButton}>
            <SecondaryButton dataTest="on-datatype" onClick={() => this.setConstraintType("onDatatype")}>On Datatype</SecondaryButton>
          </span>
          <span className={classes.optionsButton}>
            <AdvancedButtons
              property="intersectionOf"
              title="Intersection"
              dataTest="intersection-of"
              onAddExpression={this.addDatatypeToList}
              onAddSimple={(property) => () => this.setConstraintType(property)}
            />
          </span>
          <span className={classes.optionsButton}>
            <AdvancedButtons
              property="unionOf"
              title="Union"
              dataTest="union-of"
              onAddExpression={this.addDatatypeToList}
              onAddSimple={(property) => () => this.setConstraintType(property)}
            />
          </span>
          <span className={classes.optionsButton}>
            <AdvancedButtons
              property="datatypeComplementOf"
              title="Complement"
              dataTest="complement-of"
              onAddExpression={(property) => () => this.addDatatype(property)}
              onAddSimple={(property) => () => this.setConstraintType(property)}
            />
          </span>
          <span className={classes.optionsButton}>
            <SecondaryButton dataTest="one-of" onClick={() => this.setConstraintType("oneOf")}>One Of</SecondaryButton>
          </span>
        </div>
      }
    </div>;

    const wrList = wrOrder ?
      wrOrder.filter(wr => wr !== undefined)
      :
      withRestrictions ?
        [...withRestrictions]
        :
        [];

    if (data.intersectionOf || data.unionOf || data.datatypeComplementOf || data.oneOf) {
      return <div
        className={items.length === 0 ? classes.incompleteData : classes.completeData}
        style={{ marginBottom: 12 }}>
        <div>
          {data.pendingDeletion &&
            <Notification error={true} text="This datatype is pending deletion." dataTest="pending-deletion" />
          }
          <div style={{ display: 'flex', width: '100%' }}>
            <div style={{ clear: 'both' }}>
              <Typography variant="caption" style={{ color: 'black' }} data-test="constraint-label">{label}</Typography>
            </div>
            {!isReadOnly &&
              <div style={{ flex: 1, textAlign: 'right' }}>
                {propName !== "oneOf" && propName !== "datatypeComplementOf" &&
                  <React.Fragment>
                    <span style={{ marginLeft: 6 }}>
                      <ComplexExpressionButton dataTest="add-complex-datatype" onClick={this.addDatatypeToList()} title="Add a complex datatype" />
                    </span>
                  </React.Fragment>
                }
                {(propName !== "oneOf" && (propName !== "datatypeComplementOf" || (items && items.length === 0))) &&
                  <span style={{ marginLeft: 6 }}>
                    <DatatypeDialog
                      sourceComponent={sourceComponent}
                      onAddDatatype={this.addResourceToList}
                      viewLang={viewLang}
                      graph={graph}
                      existingValues={items}
                      title="Add Datatype"
                      name="Datatype" />
                  </span>
                }
                {(propName === "oneOf") && (!items || items.length < 100) &&
                  <span style={{ marginLeft: 6 }}>
                    <TypedLiteralDialog
                      key={currentRange}
                      dataTest="add-one-of-value"
                      datatypeFixed={currentRange ? true : false}
                      datatype={currentRange}
                      onAddLiteral={this.addLiteralToList}
                      existingValues={existingTypedLiteralValues}
                      title="Add Literal To One Of"
                      placeholder="Enter value"
                      name="Text" />
                  </span>
                }
                <span style={{ marginLeft: 6 }}>
                  <Tooltip title='Delete'>
                    <span style={{ marginBottom: 6 }}>
                      <IconButton data-test="delete-list" size="small" onClick={this.deleteResource} className={classes.deleteButton}>
                        <DeleteIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </span>
              </div>
            }
          </div>
          {propName === "intersectionOf" &&
            <div style={{ marginBottom: 12, clear: 'both', marginTop: 6 }}>
              <Notification dataTest="intersection-warning" text="Intersections are highly likely to result in empty sets." />
            </div>
          }
          {(propName === "unionOf" || propName === "intersectionOf") && itemsList.length === 1 &&
            <div style={{ marginBottom: 12, clear: 'both', marginTop: 6 }}>
              <Notification text="For this to be needed there should be at least two items." dataTest="at-least-two" />
            </div>
          }
          <div data-test="literal-values">
            {itemsList.map((item, itemIndex) => propName === "oneOf" ?
              <DatatypeDisplay
                key={itemIndex}
                title=""
                datatype={item["type"]}
                defaultLang={defaultLang}
                viewLang={viewLang}
                projectLang={[defaultLang]}
                property="oneOf"
                values={item ? [item] : []}
                associatedProjectGraph={graph}
                context={context}
                projectIRI={projectIRI}
                graph={graph}
                sourceComponent={sourceComponent}
                isReadOnly={true} />
              :
              item.startsWith(BN_NAMESPACE) ?
                <BlankNodeHandler
                  key={item}
                  id={item}
                  handleNotAvailable={true}
                  context={context}
                  parent={data}
                  sourceComponent={sourceComponent}
                  graph={graph}
                  projectIRI={projectIRI}
                  components={components}
                  isReadOnly={isReadOnly}
                  viewLang={viewLang}
                  defaultLang={defaultLang} />
                :
                <CommonIriDisplay
                  id={item}
                  key={item}
                  onDeleteIRI={() => this.deleteListItem(propName, itemIndex, id)}
                  components={components}
                  projectIRI={projectIRI}
                  handleNotAvailable={true}
                  property={propName}
                  isReadOnly={propName === "datatypeComplementOf" || isReadOnly}
                  compact={true}
                  graph={graph}
                  source={id}
                  wrap={true}
                  viewLang={viewLang}
                  defaultLang={defaultLang} />
            )}
          </div>
        </div>
      </div>;
    }

    return (
      <div className={classes.completeDataDark} style={{ marginBottom: 12 }}>
        <div>
          {operator ?
            <TypedLiteralDialog
              onAddLiteral={this.addWith}
              title={operatorTitle}
              open={true}
              includeOWL={true}
              datatypeFixed={true}
              onExitLiteral={this.removeOperator}
              placeholder="Enter value"
              datatype={["minLength", "maxLength", "length"].includes(operator) ?
                "http://www.w3.org/2001/XMLSchema#nonNegativeInteger"
                :
                operator === "pattern" ?
                  "http://www.w3.org/2001/XMLSchema#string"
                  :
                  onDatatype}
              name="Value" />
            :
            <div>
              {!isReadOnly &&
                <Tooltip title='Delete datatype'>
                  <span style={{ float: 'right', marginBottom: 6 }}>
                    <Typography variant="body2">
                      <IconButton data-test="delete-datatype" size="small" onClick={this.deleteResource} className={classes.deleteButton}>
                        <DeleteIcon />
                      </IconButton>
                    </Typography>
                  </span>
                </Tooltip>
              }
              <div style={{ marginBottom: 6 }}>
                <Typography data-test="on-datatype-constraint" variant="caption" style={{ color: 'black' }}>On Datatype</Typography>
              </div>
              <CommonIriDisplay
                key={onDatatype}
                id={onDatatype}
                components={components}
                projectIRI={projectIRI}
                handleNotAvailable={true}
                property="onDatatype"
                isReadOnly={true}
                compact={true}
                graph={graph}
                source={id}
                wrap={false}
                viewLang={viewLang}
                defaultLang={defaultLang} />
              <div style={{ marginBottom: 6 }}>
                <Typography variant="caption" style={{ color: 'black' }}>With Restrictions{!isComplete && ' (Optional)'}</Typography>
                {!isReadOnly &&
                  <div>
                    {datatypeFormat === NUMBER_DATATYPE ?
                      <React.Fragment>
                        {!used[0] && !used[2] &&
                          <Tooltip title="Minimum inclusive">
                            <span className={classes.optionsButton}>
                              <SecondaryButton dataTest="min-inclusive" onClick={() => this.selectOperator("minInclusive")}>&gt;=</SecondaryButton>
                            </span>
                          </Tooltip>
                        }
                        {!used[1] && !used[3] &&
                          <Tooltip title="Maximum inclusive">
                            <span className={classes.optionsButton}>
                              <SecondaryButton dataTest="max-inclusive" onClick={() => this.selectOperator("maxInclusive")}>&lt;=</SecondaryButton>
                            </span>
                          </Tooltip>
                        }
                        {!used[2] && !used[0] &&
                          <Tooltip title="Minimum exclusive">
                            <span className={classes.optionsButton}>
                              <SecondaryButton dataTest="min-exclusive" onClick={() => this.selectOperator("minExclusive")}>&gt;</SecondaryButton>
                            </span>
                          </Tooltip>
                        }
                        {!used[3] && !used[1] &&
                          <Tooltip title="Maximum exclusive">
                            <span className={classes.optionsButton}>
                              <SecondaryButton dataTest="max-exclusive" onClick={() => this.selectOperator("maxExclusive")}>&lt;</SecondaryButton>
                            </span>
                          </Tooltip>
                        }
                      </React.Fragment>
                      :
                      <React.Fragment>
                        {!used[0] && !used[2] &&
                          <Tooltip title="Minimum length">
                            <span className={classes.optionsButton}>
                              <SecondaryButton dataTest="min-length" onClick={() => this.selectOperator("minLength")}>Min. Length</SecondaryButton>
                            </span>
                          </Tooltip>
                        }
                        {!used[1] && !used[2] &&
                          <Tooltip title="Maximum length">
                            <span className={classes.optionsButton}>
                              <SecondaryButton dataTest="max-length" onClick={() => this.selectOperator("maxLength")}>Max. Length</SecondaryButton>
                            </span>
                          </Tooltip>
                        }
                        {!used[0] && !used[1] && !used[2] &&
                          <Tooltip title="Length">
                            <span className={classes.optionsButton}>
                              <SecondaryButton dataTest="length" onClick={() => this.selectOperator("length")}>Length</SecondaryButton>
                            </span>
                          </Tooltip>
                        }
                        {!used[3] &&
                          <Tooltip title="Pattern">
                            <span className={classes.optionsButton}>
                              <SecondaryButton dataTest="pattern" onClick={() => this.selectOperator("pattern")}>Pattern</SecondaryButton>
                            </span>
                          </Tooltip>
                        }
                      </React.Fragment>
                    }
                  </div>
                }
                <div data-test="constraint-list">
                  {withRestrictions && wrList.map(wr => {
                    return <BlankNodeHandler
                      key={wr}
                      id={wr}
                      onDelete={() => this.removeWith(withRestrictions.indexOf(wr))}
                      handleNotAvailable={true}
                      context={context}
                      sourceComponent={sourceComponent}
                      useWorkflow={useWorkflow}
                      graph={graph}
                      projectIRI={projectIRI}
                      components={components}
                      isReadOnly={isReadOnly}
                      viewLang={viewLang}
                      defaultLang={defaultLang} />;
                  })}
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    );
  }
}

DatatypeHandler.propTypes = {
  id: PropTypes.string,
  data: PropTypes.object,
  defaultLang: PropTypes.string,
  viewLang: PropTypes.string,
  sourceComponent: PropTypes.string.isRequired,
  useWorkflow: PropTypes.bool,
  currentRange: PropTypes.string,
  components: PropTypes.array,
  graph: PropTypes.string,
  projectIRI: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool
};

export default withStyles(styles)(withGS(DatatypeHandler));